import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { TurnScore } from "@a2/core";
import type { Question } from "../../components/exam/types";
import type { ComposerSubmission } from "../../components/exam/Composer";
import type { TurnResponse, PrepareMediaResponse } from "../../../../api/src/routes/exam";
import { authedFetch } from "../api-client";

type ApiEnvelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };

export type SessionMode = "full" | "deel-1" | "deel-2" | "deel-3" | "deel-4";

/**
 * Two-state runner mirroring real DUO Spreken UX:
 *   answering → feedback (post-submit view, possibly grading-in-flight)
 */
export type Stage =
  | { kind: "answering" }
  | { kind: "feedback"; viewing: number };

export type UseExamSessionInput = {
  sessionId: string;
  initialQuestions: Question[];
  questionImageAlts: Record<string, string[]>;
  mode: SessionMode;
  skin: "ember" | "duo";
  focusMode?: boolean;
  initialCompleted?: Record<number, TurnResponse>;
};

export type ExamSessionActions = {
  submit: (sub: ComposerSubmission) => void;
  retryCurrent: () => void;
  jumpToIndex: (idx: number) => void;
  setFocusMode: (next: boolean) => void;
  confirmExit: () => void;
  requestExit: () => void;
  cancelExit: () => void;
  onRecordingChange: (rec: boolean) => void;
  onAudioRetry: () => void;
  onTimeUp: () => void;
  onOneMinuteWarning: () => void;
  finalize: () => void;
  setRecordedStream: (stream: MediaStream | null) => void;
  setRecordedBlob: (blob: Blob | null) => void;
  deleteRecording: () => void;
  onFifteenMinWarning: () => void;
  dismissFifteenMinWarning: () => void;
  toggleBookmark: (idx: number) => void;
  setSkin: (next: "ember" | "duo") => Promise<void>;
};

export type ExamSessionState = {
  sessionId: string;
  mode: SessionMode;
  skin: "ember" | "duo";
  focusMode: boolean;

  questions: Question[];
  current: number;
  stage: Stage;
  displayedIndex: number;
  question: Question;
  isLastQuestion: boolean;
  prevIdx: number | null;
  nextIdx: number | null;
  isFirstUnanswered: boolean;

  completed: Record<number, TurnResponse>;
  pendingSubmits: Set<number>;
  submitErrors: Record<number, string>;

  visited: Set<number>;
  isVisited: (i: number) => boolean;
  isAnswered: (i: number) => boolean;
  isGrading: (i: number) => boolean;
  scoreFor: (i: number) => number | null;
  adFor: (i: number) => number | null;
  frontier: number;

  turn: TurnResponse | null;
  score: TurnScore | null;
  viewingIdx: number | null;
  viewingPending: boolean;
  viewingError: string | undefined;

  timerRunning: boolean;
  recordingAttempt: number;
  recordedStream: MediaStream | null;
  recordedBlob: Blob | null;

  timeUp: boolean;
  oneMinuteWarning: boolean;
  oneMinuteToast: boolean;

  audioFailedIdx: Set<number>;

  exitOpen: boolean;

  fifteenMinWarningOpen: boolean;

  bookmarks: Set<number>;
  deferFeedback: boolean;
};

export type UseExamSessionReturn = ExamSessionState & ExamSessionActions;

function bookmarksStorageKey(sessionId: string): string {
  return `a2duo:bookmarks:${sessionId}`;
}

function readStoredBookmarks(sessionId: string): Set<number> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(bookmarksStorageKey(sessionId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    const ints = parsed.filter(
      (v): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0,
    );
    return new Set(ints);
  } catch {
    return new Set();
  }
}

function writeStoredBookmarks(sessionId: string, bookmarks: Set<number>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      bookmarksStorageKey(sessionId),
      JSON.stringify([...bookmarks]),
    );
  } catch {
    // Quota / private mode — accept loss across refresh.
  }
}

export function useExamSession(input: UseExamSessionInput): UseExamSessionReturn {
  const {
    sessionId,
    initialQuestions,
    questionImageAlts,
    mode,
    skin: initialSkin,
    focusMode: initialFocusMode = false,
    initialCompleted = {},
  } = input;

  const initialCurrent = (() => {
    for (let i = 0; i < initialQuestions.length; i++) {
      if (initialCompleted[i] === undefined) return i;
    }
    return Math.max(0, initialQuestions.length - 1);
  })();
  const initialStage: Stage =
    initialCompleted[initialCurrent] !== undefined
      ? { kind: "feedback", viewing: initialCurrent }
      : { kind: "answering" };

  const [current, setCurrent] = useState(initialCurrent);
  const [stage, setStage] = useState<Stage>(initialStage);
  const [completed, setCompleted] =
    useState<Record<number, TurnResponse>>(initialCompleted);
  const [submitErrors, setSubmitErrors] = useState<Record<number, string>>({});
  const [timerRunning, setTimerRunning] = useState(false);
  const [recordingAttempt, setRecordingAttempt] = useState(0);
  const [focusMode, setFocusModeState] = useState(initialFocusMode);
  const [pendingSubmits, setPendingSubmits] = useState<Set<number>>(() => new Set());
  const submitEpochRef = useRef<Map<number, number>>(new Map());
  const [visited, setVisited] = useState<Set<number>>(() => {
    const seed = new Set<number>([0, initialCurrent]);
    for (const k of Object.keys(initialCompleted)) seed.add(Number(k));
    return seed;
  });
  const [questions, setQuestions] = useState<Question[]>(initialQuestions);
  const preparingRef = useRef<Set<number>>(new Set());
  const [audioFailedIdx, setAudioFailedIdx] = useState<Set<number>>(new Set());
  const [timeUp, setTimeUp] = useState(false);
  const [oneMinuteWarning, setOneMinuteWarning] = useState(false);
  const [oneMinuteToast, setOneMinuteToast] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [fifteenMinWarningOpen, setFifteenMinWarningOpen] = useState(false);
  const fifteenMinWarningFiredRef = useRef(false);
  const [recordedStream, setRecordedStreamState] = useState<MediaStream | null>(null);
  const [recordedBlob, setRecordedBlobState] = useState<Blob | null>(null);
  const setRecordedStream = useCallback((stream: MediaStream | null) => {
    setRecordedStreamState(stream);
  }, []);
  const setRecordedBlob = useCallback((blob: Blob | null) => {
    setRecordedBlobState(blob);
  }, []);
  const deleteRecording = useCallback(() => {
    setRecordedBlobState(null);
  }, []);
  const onFifteenMinWarning = useCallback(() => {
    if (fifteenMinWarningFiredRef.current) return;
    fifteenMinWarningFiredRef.current = true;
    setFifteenMinWarningOpen(true);
  }, []);
  const dismissFifteenMinWarning = useCallback(() => {
    setFifteenMinWarningOpen(false);
  }, []);
  const [skin, setSkinState] = useState<"ember" | "duo">(initialSkin);
  const [bookmarks, setBookmarks] = useState<Set<number>>(() =>
    readStoredBookmarks(sessionId),
  );
  const toggleBookmark = useCallback(
    (idx: number) => {
      setBookmarks((prev) => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        writeStoredBookmarks(sessionId, next);
        return next;
      });
    },
    [sessionId],
  );
  const setSkin = useCallback(
    async (next: "ember" | "duo") => {
      setSkinState(next);
      if (next === "duo") {
        setFocusModeState((prev) => prev || true);
      }
      try {
        const res = await authedFetch("/exam/session/skin", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sessionId, skin: next }),
        });
        if (!res.ok) {
          console.warn(
            `[exam] skin persist failed (status=${res.status}); UI shows ${next} but server still has previous value.`,
          );
        }
      } catch (err) {
        console.warn(
          `[exam] skin persist network error: ${err instanceof Error ? err.message : "?"}`,
        );
      }
    },
    [sessionId],
  );
  const navigate = useNavigate();

  const retryEnabled = true;

  const prepareMedia = useCallback(
    async (index: number) => {
      if (index < 0 || index >= questions.length) return;
      if (preparingRef.current.has(index)) return;
      const target = questions[index];
      if (!target) return;
      const hasImage =
        target.imageUrls.length > 0 || (questionImageAlts[target.id]?.length ?? 0) === 0;
      if (target.questionAudioUrl && hasImage) return;
      preparingRef.current.add(index);
      try {
        const res = await authedFetch("/exam/prepare-media", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sessionId, questionId: target.id }),
        });
        const json = (await res.json()) as ApiEnvelope<PrepareMediaResponse>;
        if (!json.ok) {
          console.warn(`[exam] prepare-media failed for ${target.id}: ${json.error.message}`);
          preparingRef.current.delete(index);
          setVisited((prev) => new Set(prev).add(index));
          return;
        }
        setQuestions((prev) => {
          const next = [...prev];
          const existing = next[index];
          if (!existing) return prev;
          next[index] = {
            ...existing,
            imageUrls: json.data.imageUrls,
            questionAudioUrl: json.data.questionAudioUrl,
          };
          return next;
        });
        if (!json.data.questionAudioUrl) {
          setAudioFailedIdx((prev) => {
            const next = new Set(prev);
            next.add(index);
            return next;
          });
        }
        setVisited((prev) => new Set(prev).add(index));
      } catch (err) {
        console.warn(`[exam] prepare-media network error: ${err instanceof Error ? err.message : "?"}`);
        preparingRef.current.delete(index);
        setVisited((prev) => new Set(prev).add(index));
      }
    },
    [questions, sessionId, questionImageAlts],
  );

  useEffect(() => {
    void prepareMedia(current + 1);
  }, [current, prepareMedia]);

  useEffect(() => {
    void prepareMedia(current);
  }, [current, prepareMedia]);

  const submit = useCallback(
    (sub: ComposerSubmission) => {
      const targetIndex = current;
      const targetQuestion = questions[targetIndex];
      if (!targetQuestion) return;
      setTimerRunning(false);
      setSubmitErrors((prev) => {
        if (!(targetIndex in prev)) return prev;
        const next = { ...prev };
        delete next[targetIndex];
        return next;
      });

      const epoch = (submitEpochRef.current.get(targetIndex) ?? 0) + 1;
      submitEpochRef.current.set(targetIndex, epoch);

      const form = new FormData();
      form.set("sessionId", sessionId);
      form.set("questionId", targetQuestion.id);
      if (sub.kind === "audio") {
        const ext = sub.blob.type.includes("mp4")
          ? "m4a"
          : sub.blob.type.includes("ogg")
            ? "ogg"
            : sub.blob.type.includes("aac")
              ? "aac"
              : "webm";
        form.set("audio", sub.blob, `answer.${ext}`);
      } else {
        form.set("text", sub.text);
      }

      setPendingSubmits((prev) => new Set(prev).add(targetIndex));
      setStage({ kind: "feedback", viewing: targetIndex });

      void (async () => {
        let json: ApiEnvelope<TurnResponse>;
        try {
          const res = await authedFetch("/exam/turn", {
            method: "POST",
            body: form,
          });
          json = (await res.json()) as ApiEnvelope<TurnResponse>;
        } catch (err) {
          if (submitEpochRef.current.get(targetIndex) !== epoch) return;
          console.warn(
            `[exam] turn ${targetIndex} network error: ${err instanceof Error ? err.message : "?"}`,
          );
          setSubmitErrors((prev) => ({
            ...prev,
            [targetIndex]:
              "Verbinding met de server is mislukt. Probeer over een paar seconden opnieuw.",
          }));
          setPendingSubmits((prev) => {
            if (!prev.has(targetIndex)) return prev;
            const next = new Set(prev);
            next.delete(targetIndex);
            return next;
          });
          return;
        }
        if (submitEpochRef.current.get(targetIndex) !== epoch) return;
        if (!json.ok) {
          console.warn(`[exam] turn ${targetIndex} failed: ${json.error.message}`);
          setSubmitErrors((prev) => ({
            ...prev,
            [targetIndex]: json.ok ? "" : json.error.message,
          }));
          setPendingSubmits((prev) => {
            if (!prev.has(targetIndex)) return prev;
            const next = new Set(prev);
            next.delete(targetIndex);
            return next;
          });
          return;
        }
        setCompleted((prev) => ({ ...prev, [targetIndex]: json.data as TurnResponse }));
        setPendingSubmits((prev) => {
          if (!prev.has(targetIndex)) return prev;
          const next = new Set(prev);
          next.delete(targetIndex);
          return next;
        });
      })();
    },
    [current, questions, sessionId],
  );

  const retryCurrent = useCallback(() => {
    if (!retryEnabled) return;
    if (stage.kind !== "feedback") return;
    const target = stage.viewing;
    const epoch = (submitEpochRef.current.get(target) ?? 0) + 1;
    submitEpochRef.current.set(target, epoch);
    setCompleted((prev) => {
      const next = { ...prev };
      delete next[target];
      return next;
    });
    setPendingSubmits((prev) => {
      if (!prev.has(target)) return prev;
      const next = new Set(prev);
      next.delete(target);
      return next;
    });
    setSubmitErrors((prev) => {
      if (!(target in prev)) return prev;
      const next = { ...prev };
      delete next[target];
      return next;
    });
    setCurrent(target);
    setStage({ kind: "answering" });
    setTimerRunning(false);
  }, [retryEnabled, stage]);

  useEffect(() => {
    if (!oneMinuteToast) return;
    const t = setTimeout(() => setOneMinuteToast(false), 4000);
    return () => clearTimeout(t);
  }, [oneMinuteToast]);

  const confirmExit = useCallback(() => {
    const completedCount = Object.keys(completed).length;
    setExitOpen(false);
    if (completedCount > 0) {
      navigate(`/report/${sessionId}?from=exam`);
    } else {
      navigate("/");
    }
  }, [completed, navigate, sessionId]);

  const requestExit = useCallback(() => {
    setExitOpen(true);
  }, []);

  const cancelExit = useCallback(() => {
    setExitOpen(false);
  }, []);

  const finalize = useCallback(() => {
    navigate(`/report/${sessionId}?from=exam`);
  }, [navigate, sessionId]);

  const displayedIndex = stage.kind === "feedback" ? stage.viewing : current;
  // Why fall back to questions[0]: under noUncheckedIndexedAccess this is
  // typed as Question | undefined. In practice displayedIndex is always
  // within bounds (clamped by jumpToIndex + initialCurrent logic) so the
  // bang-with-fallback is a TS appeasement, not a runtime safety hatch.
  const question: Question = questions[displayedIndex] ?? questions[0]!;
  const isLastQuestion = displayedIndex === questions.length - 1;
  const prevIdx = displayedIndex > 0 ? displayedIndex - 1 : null;
  const nextIdx =
    displayedIndex < questions.length - 1 ? displayedIndex + 1 : null;

  const jumpToIndex = useCallback(
    (idx: number) => {
      if (
        skin === "duo" &&
        idx !== current &&
        recordedBlob !== null &&
        completed[current] === undefined &&
        !pendingSubmits.has(current)
      ) {
        submit({ kind: "audio", blob: recordedBlob });
      }
      setRecordedBlobState(null);
      setRecordedStreamState(null);
      if (completed[idx] !== undefined || pendingSubmits.has(idx)) {
        setStage({ kind: "feedback", viewing: idx });
      } else {
        setCurrent(idx);
        setStage({ kind: "answering" });
      }
      setTimerRunning(false);
    },
    [completed, pendingSubmits, skin, recordedBlob, submit, current],
  );

  const turn = stage.kind === "feedback" ? (completed[stage.viewing] ?? null) : null;
  const score: TurnScore | null = turn
    ? {
        ad: turn.score.ad,
        wo: turn.score.wo,
        gr: turn.score.gr,
        vl: turn.score.vl,
        helderheid: turn.score.helderheid,
        uit: turn.score.uit,
        uitNotes: turn.score.uitNotes,
      }
    : null;

  const completedIndices = Object.keys(completed)
    .map(Number)
    .sort((a, b) => a - b);
  const isFirstUnanswered = current === 0 && completedIndices.length === 0;

  const isVisited = useCallback((i: number) => visited.has(i), [visited]);
  const isAnswered = useCallback(
    (i: number) => completed[i] !== undefined,
    [completed],
  );
  const isGrading = useCallback(
    (i: number) => pendingSubmits.has(i),
    [pendingSubmits],
  );
  const scoreFor = useCallback(
    (i: number): number | null => {
      const t = completed[i];
      if (!t) return null;
      return (
        t.score.ad + t.score.wo + t.score.gr + t.score.vl + t.score.helderheid + t.score.uit
      );
    },
    [completed],
  );
  const adFor = useCallback(
    (i: number): number | null => completed[i]?.score.ad ?? null,
    [completed],
  );

  const viewingIdx = stage.kind === "feedback" ? stage.viewing : null;
  const viewingPending = viewingIdx !== null && pendingSubmits.has(viewingIdx);
  const viewingError = viewingIdx !== null ? submitErrors[viewingIdx] : undefined;

  const frontier = visited.size > 0 ? Math.max(...visited) + 1 : 0;

  const onRecordingChange = useCallback((rec: boolean) => {
    if (rec) {
      setRecordingAttempt((n) => n + 1);
      setTimerRunning(true);
    } else {
      setTimerRunning(false);
    }
  }, []);

  const onAudioRetry = useCallback(() => {
    setAudioFailedIdx((prev) => {
      if (!prev.has(displayedIndex)) return prev;
      const next = new Set(prev);
      next.delete(displayedIndex);
      return next;
    });
    preparingRef.current.delete(displayedIndex);
    void prepareMedia(displayedIndex);
  }, [displayedIndex, prepareMedia]);

  const onTimeUp = useCallback(() => {
    setTimeUp(true);
    setTimerRunning(false);
    setTimeout(() => {
      navigate(`/report/${sessionId}?from=exam`);
    }, 1500);
  }, [navigate, sessionId]);

  const onOneMinuteWarning = useCallback(() => {
    setOneMinuteWarning(true);
    setOneMinuteToast(true);
  }, []);

  const setFocusMode = useCallback(
    (next: boolean) => {
      setFocusModeState(next);
      void authedFetch("/exam/session/focus", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sessionId, focusMode: next }),
      }).catch((err: unknown) => {
        console.warn(
          `[exam] focus-mode persist failed (${err instanceof Error ? err.message : "?"})`,
        );
      });
    },
    [sessionId],
  );

  return {
    sessionId,
    mode,
    skin,
    focusMode,

    questions,
    current,
    stage,
    displayedIndex,
    question,
    isLastQuestion,
    prevIdx,
    nextIdx,
    isFirstUnanswered,

    completed,
    pendingSubmits,
    submitErrors,

    visited,
    isVisited,
    isAnswered,
    isGrading,
    scoreFor,
    adFor,
    frontier,

    turn,
    score,
    viewingIdx,
    viewingPending,
    viewingError,

    timerRunning,
    recordingAttempt,
    recordedStream,
    recordedBlob,

    timeUp,
    oneMinuteWarning,
    oneMinuteToast,

    audioFailedIdx,

    exitOpen,

    fifteenMinWarningOpen,

    bookmarks,
    deferFeedback: skin === "duo",

    submit,
    retryCurrent,
    jumpToIndex,
    setFocusMode,
    confirmExit,
    requestExit,
    cancelExit,
    onRecordingChange,
    onAudioRetry,
    onTimeUp,
    onOneMinuteWarning,
    finalize,
    setRecordedStream,
    setRecordedBlob,
    deleteRecording,
    onFifteenMinWarning,
    dismissFifteenMinWarning,
    toggleBookmark,
    setSkin,
  };
}
